import {
  createContext,
  use,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

const AnnounceContext = createContext<(message: string) => void>(() => {});

/** How long a message stays on screen: at least 5 s, longer for longer messages. */
const visibleFor = (text: string) => Math.max(5000, text.length * 70);

/**
 * One polite live region for the whole app, for results of actions whose effect is not otherwise
 * announced (a page moved to the trash, a page moved). It is visible, so sighted users get the same
 * confirmation (WCAG 4.1.3). It clears itself after a few seconds; hovering it keeps it (WCAG 1.4.13).
 */
export function AnnouncerProvider({ children }: { children: ReactNode }) {
  const [message, setMessage] = useState("");
  const [hovered, setHovered] = useState(false);
  const frame = useRef(0);
  const announce = useCallback((text: string) => {
    // Clearing first makes a repeated identical message announce again.
    setMessage("");
    cancelAnimationFrame(frame.current);
    frame.current = requestAnimationFrame(() => setMessage(text));
  }, []);
  useEffect(() => {
    if (message === "" || hovered) return;
    const timer = setTimeout(() => setMessage(""), visibleFor(message));
    return () => clearTimeout(timer);
  }, [message, hovered]);
  return (
    <AnnounceContext value={announce}>
      {children}
      <p
        role="status"
        className="toast"
        data-empty={message === "" || undefined}
        onPointerEnter={() => setHovered(true)}
        onPointerLeave={() => setHovered(false)}
      >
        {message}
      </p>
    </AnnounceContext>
  );
}

export const useAnnounce = () => use(AnnounceContext);
